import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';

import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import { Field, inputClass } from '../../components/Field';
import { FormProblem } from '../../components/FormProblem';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { useAuth } from '../auth/AuthProvider';
import {
  clientsKey,
  createClient,
  deleteClient,
  formatDocument,
  listSites,
  situationLabels,
  situationTone,
  sitesKey,
  updateClient,
  type Client,
  type ClientBody,
  type Site,
  type Situation,
} from './api';
import { noProblem, toProblem, type Problem } from './problem';

interface Props {
  client?: Client;
  canManage: boolean;
  onClose: () => void;
  /** Shows the client just created (to add its accounts). */
  onOpen: (c: Client) => void;
  /** Opens an account of the client (or a new one). */
  onAccount: (site: Site | null) => void;
}

/** A client and its accounts (ADR-026). */
export function ClientDrawer({ client, canManage, onClose, onOpen, onAccount }: Props) {
  const { request } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(client?.name ?? '');
  const [document, setDocument] = useState(formatDocument(client?.document ?? null));
  const [status, setStatus] = useState<Situation>(client?.status ?? 'active');
  const [notes, setNotes] = useState(client?.notes ?? '');
  const [problem, setProblem] = useState<Problem>(noProblem);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const accounts = useQuery({
    queryKey: [...sitesKey, 'client', client?.id ?? ''],
    queryFn: async () =>
      (await listSites(request, { client: client?.id ?? '', page: 1, size: 100 })).items,
    enabled: !!client,
  });

  const save = useMutation({
    mutationFn: () => {
      if (!client) return createClient(request, { name, document, status, notes });
      const body: ClientBody = {};
      if (name.trim() !== client.name) body.name = name;
      // The server keeps only the digits; "" clears it.
      if (document.trim() !== formatDocument(client.document)) body.document = document;
      if (status !== client.status) body.status = status;
      if (notes.trim() !== client.notes) body.notes = notes;
      return updateClient(request, client.id, body);
    },
    onSuccess: async (c) => {
      await qc.invalidateQueries({ queryKey: clientsKey });
      notify(client ? 'Cliente atualizado.' : `Cliente ${c.name} criado. Cadastre as contas dele.`);
      if (client) onClose();
      else onOpen(c);
    },
    onError: (err) => {
      setProblem(toProblem(err));
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteClient(request, client?.id ?? ''),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: clientsKey });
      notify('Cliente excluído.');
      onClose();
    },
    onError: (err) => {
      setConfirmDelete(false);
      setProblem(toProblem(err));
    },
  });

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    setProblem(noProblem);
    save.mutate();
  };
  const readOnly = !canManage;

  return (
    <Drawer
      title={client ? client.name : 'Novo cliente'}
      description={
        client
          ? `${String(client.account_count)} conta(s) · ${String(client.device_count)} dispositivo(s)`
          : 'Depois de criar, cadastre as contas (locais monitorados) do cliente.'
      }
      onClose={onClose}
      footer={
        <>
          {client && canManage && (
            <Button
              className="mr-auto text-crit!"
              onClick={() => {
                setConfirmDelete(true);
              }}
            >
              Excluir
            </Button>
          )}
          <Button onClick={onClose}>{readOnly ? 'Fechar' : 'Cancelar'}</Button>
          {canManage && (
            <Button variant="primary" type="submit" form="client-form" disabled={save.isPending}>
              {save.isPending ? 'Salvando…' : client ? 'Salvar alterações' : 'Criar cliente'}
            </Button>
          )}
        </>
      }
    >
      <form id="client-form" onSubmit={submit} noValidate className="space-y-4">
        <FormProblem message={problem.form} />
        <Field label="Nome ou razão social" error={problem.fields.name}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              data-autofocus
              readOnly={readOnly}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            />
          )}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="CPF ou CNPJ"
            hint="Opcional; conferido pelos dígitos."
            error={problem.fields.document}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                readOnly={readOnly}
                inputMode="numeric"
                value={document}
                onChange={(e) => {
                  setDocument(e.target.value);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={`${inputClass} font-mono`}
              />
            )}
          </Field>
          <Field label="Situação" error={problem.fields.status}>
            {({ id, describedBy, invalid }) => (
              <select
                id={id}
                disabled={readOnly}
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as Situation);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={inputClass}
              >
                {(Object.keys(situationLabels) as Situation[]).map((s) => (
                  <option key={s} value={s}>
                    {situationLabels[s]}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        <Field label="Observações" error={problem.fields.notes}>
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              readOnly={readOnly}
              rows={2}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
              }}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              className={inputClass}
            />
          )}
        </Field>
      </form>

      {client && (
        <section
          aria-labelledby="accounts-heading"
          className="mt-6 space-y-3 border-t border-line pt-5"
        >
          <div className="flex items-center justify-between gap-3">
            <h3 id="accounts-heading" className="text-[13px] font-medium text-fg">
              Contas (locais monitorados)
            </h3>
            {canManage && (
              <Button
                className="h-7"
                onClick={() => {
                  onAccount(null);
                }}
              >
                <Plus aria-hidden="true" className="size-3.5" strokeWidth={2} />
                Nova conta
              </Button>
            )}
          </div>
          {accounts.isPending ? (
            <p role="status" className="text-xs text-fg-muted">
              Carregando contas…
            </p>
          ) : accounts.isError ? (
            <p role="alert" className="text-xs text-crit">
              Não foi possível carregar as contas.
            </p>
          ) : accounts.data.length === 0 ? (
            <p className="text-xs text-warn">
              Nenhuma conta ainda: cadastre ao menos uma para ligar câmeras e gravadores.
            </p>
          ) : (
            <ul
              aria-label={`Contas de ${client.name}`}
              className="divide-y divide-line rounded-md border border-line"
            >
              {accounts.data.map((a) => (
                <li key={a.id} className="flex items-center gap-3 px-3 py-2">
                  <MapPin aria-hidden="true" className="size-4 shrink-0 text-fg-subtle" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] text-fg">{a.name}</div>
                    <div className="truncate text-2xs text-fg-subtle">
                      {[a.address, `${String(a.device_count)} dispositivo(s)`]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  {a.status !== 'active' && (
                    <StatusBadge tone={situationTone[a.status]}>
                      {situationLabels[a.status].replace(/o$/, 'a')}
                    </StatusBadge>
                  )}
                  <Button
                    variant="ghost"
                    className="h-7"
                    aria-label={`${canManage ? 'Editar' : 'Ver'} conta ${a.name}`}
                    onClick={() => {
                      onAccount(a);
                    }}
                  >
                    {canManage ? 'Editar' : 'Ver'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {confirmDelete && client && (
        <ConfirmDialog
          title={`Excluir ${client.name}?`}
          confirmLabel="Excluir cliente"
          busy={remove.isPending}
          onCancel={() => {
            setConfirmDelete(false);
          }}
          onConfirm={() => {
            remove.mutate();
          }}
        >
          O cliente só pode ser excluído quando não tiver contas. Esta ação não pode ser desfeita.
        </ConfirmDialog>
      )}
    </Drawer>
  );
}
